import React from 'react';
import { View, StyleSheet, Text, StyleProp, ViewStyle } from 'react-native';
import Svg, { Circle, Defs, RadialGradient, Stop } from 'react-native-svg';

interface TactileCircleTokenProps {
    color?: 'blue' | 'red' | 'green' | 'yellow';
    size?: number;
    badgeValue?: number | string;
    style?: StyleProp<ViewStyle>;
    testID?: string;
}

const colorTokens = {
    blue: {
        rim: '#93C5FD',
        ring: '#2563EB',
        core: '#3B82F6',
        badgeBg: '#1D4ED8',
    },
    red: {
        rim: '#FCA5A5',
        ring: '#DC2626',
        core: '#EF4444',
        badgeBg: '#B91C1C',
    },
    green: {
        rim: '#86EFAC',
        ring: '#16A34A',
        core: '#22C55E',
        badgeBg: '#15803D',
    },
    yellow: {
        rim: '#FDE68A',
        ring: '#D97706',
        core: '#F59E0B',
        badgeBg: '#B45309',
    },
};

/**
 * TactileCircleToken — Concentric 3D tactile circular counter.
 * Supports docked mini-badges (e.g. the red circle with mini (6) badge in the reference screenshot).
 */
export const TactileCircleToken: React.FC<TactileCircleTokenProps> = ({
    color = 'blue',
    size = 54,
    badgeValue,
    style,
    testID = 'tactile-circle-token',
}) => {
    const theme = colorTokens[color];
    const gradId = `tokenGlow_${color}_${size}`;
    const badgeSize = Math.round(size * 0.42);

    return (
        <View testID={testID} style={[styles.wrapper, { width: size, height: size }, style]}>
            <Svg width={size} height={size} viewBox="0 0 60 60">
                <Defs>
                    <RadialGradient id={gradId} cx="25" cy="25" r="28" gradientUnits="userSpaceOnUse">
                        <Stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.45" />
                        <Stop offset="65%" stopColor={theme.core} stopOpacity="1" />
                        <Stop offset="100%" stopColor={theme.ring} stopOpacity="1" />
                    </RadialGradient>
                </Defs>

                {/* Outer Rim */}
                <Circle cx="30" cy="30" r="28" fill={theme.rim} opacity={0.6} />
                {/* Mid Ring */}
                <Circle cx="30" cy="30" r="24" fill={theme.ring} />
                {/* Inner Core with Inset Highlight */}
                <Circle cx="30" cy="29" r="19" fill={`url(#${gradId})`} />
                {/* Center depression ring */}
                <Circle cx="30" cy="29" r="14" fill="none" stroke="rgba(255, 255, 255, 0.3)" strokeWidth="1.5" />
            </Svg>

            {/* Optional Docked Mini Badge (e.g. mini "6") */}
            {badgeValue !== undefined && (
                <View
                    style={[
                        styles.badge,
                        {
                            width: badgeSize,
                            height: badgeSize,
                            borderRadius: badgeSize / 2,
                            backgroundColor: theme.badgeBg,
                        },
                    ]}
                >
                    <Text
                        allowFontScaling={true}
                        maxFontSizeMultiplier={1.3}
                        style={[styles.badgeText, { fontSize: Math.max(10, Math.round(badgeSize * 0.58)) }]}
                    >
                        {badgeValue}
                    </Text>
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    wrapper: {
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.15,
        shadowRadius: 4,
        elevation: 3,
    },
    badge: {
        position: 'absolute',
        bottom: -2,
        right: -2,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderColor: '#FFFFFF',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
        elevation: 4,
    },
    badgeText: {
        color: '#FFFFFF',
        fontWeight: '900',
    },
});
